/**
 * CropRotateEditor — a modal that CROPS + ROTATES a source image and BAKES a
 * derived raster (the canonical tracing + report surface). Non-destructive: it
 * edits the PRISTINE source (never the prior baked output), so repeated edits
 * never compound rotation/crop loss; the original upload is untouched.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WORKFLOW: rotated maps arrive at random angles. Staff rotate (45° / 90°
 *  quick + arbitrary numeric + ±1° nudge) ONLY to STRAIGHTEN the tilted scan,
 *  then draw a normal UPRIGHT crop box around the part they want. On "Apply" the
 *  recipe (ROTATE → CROP) is baked into a NEW image; the parent uploads it and
 *  points the tracer + report at it (zero coordinate transform — see
 *  lib/imageCropRotate.ts for the bake-vs-transform rationale).
 *
 *  ROTATE-THEN-CROP / AXIS-ALIGNED OVERLAY (owner-locked 2026-06-14): the owner
 *  rotates to straighten, then wants a NORMAL upright crop window — "I never
 *  asked you to rotate the crop window". So the crop SVG overlay is AXIS-ALIGNED
 *  to the screen at EVERY rotation (it does NOT tilt with the image). The user
 *  draws an upright rectangle over the rotated image; `bakeImage` rotates the
 *  source first THEN crops that upright rect out of the straightened image.
 *
 *  CROP PERSISTS THROUGH ROTATION (owner-locked 2026-06-15): the image rotates
 *  UNDER a fixed, upright crop box that STAYS PUT and keeps showing the region
 *  inside it as the angle changes — the crop is NOT wiped when the user
 *  straightens. As the displayed (rotated-bbox) frame reshapes with the angle,
 *  the crop is re-mapped into the new frame (`rescaleCropToFrame` — proportional
 *  rescale + clamp) so it stays sensible/visible, rather than discarded. This
 *  reverses the earlier "clear the crop on any rotation change" behaviour, which
 *  the owner rejected.
 *
 *  COORDINATE SPACE: the crop rect is held in the DISPLAYED (rotated-bounding-
 *  box) frame — W'×H' where W'=|W·cos|+|H·sin|, H'=|W·sin|+|H·cos|. The crop SVG
 *  viewBox is `0 0 W' H'` (axis-aligned, NO transform); the stage box is sized
 *  to W':H' so the user can reach every corner of the rotated image; the preview
 *  <img> is rotated + centred INSIDE that box (its rotated bbox fills the box).
 *  `toSrcPt` maps a click → rotated-bbox-frame coords (the crop space); the
 *  authoritative pixels are produced by `bakeImage` (rotate→crop) on Apply.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Btn3 } from './ui';
import {
  type CropDrag,
  type CropRect,
  type EditRecipe,
  bakedSize,
  clampCrop,
  normaliseDeg,
  recipeIsIdentity,
  rescaleCropToFrame,
  resolveDrag,
  rotatedBBox,
} from '../lib/imageCropRotate';

type Corner = 0 | 1 | 2 | 3; // TL, TR, BR, BL

/** The minimal client-coordinate shape a drag handler needs — satisfied by a
 *  MouseEvent, a React mouse event, OR a Touch (so one code path serves both
 *  desktop mouse and mobile/Capacitor touch). */
interface ClientPt {
  clientX: number;
  clientY: number;
}

/** Pull the first changed Touch (or null) from a TouchEvent — the drag tracks a
 *  single finger. */
function firstTouch(e: TouchEvent): Touch | null {
  return e.changedTouches && e.changedTouches.length > 0
    ? e.changedTouches[0]
    : (e.touches && e.touches.length > 0 ? e.touches[0] : null);
}

/** PURE-ish — map a client point to DISPLAYED-frame (rotated-bbox) coords via the
 *  SVG CTM. The SVG is axis-aligned (no rotation), viewBox `0 0 W' H'`, so this
 *  returns coords in the crop space `bakeImage` (rotate→crop) crops against. */
function toSrcPt(
  svg: SVGSVGElement | null,
  e: ClientPt,
): { x: number; y: number } | null {
  if (!svg) return null;
  const pt = svg.createSVGPoint();
  pt.x = e.clientX;
  pt.y = e.clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const p = pt.matrixTransform(ctm.inverse());
  return { x: p.x, y: p.y };
}

export default function CropRotateEditor({
  imageUrl,
  srcWidth,
  srcHeight,
  initialRecipe,
  title = 'Crop & rotate',
  busy = false,
  preparing = false,
  loadError = null,
  onApply,
  onCancel,
}: {
  /** the PRISTINE source image URL (original upload, never the baked output).
   *  When the parent is still resolving the source (PDF rasterise / blob fetch),
   *  this is '' and `preparing` is true → the editor shows a "Preparing…" state. */
  imageUrl: string;
  /** natural pixel size of the source. Pass 0/0 if unknown — the editor reads
   *  the image's natural size on load (e.g. the north screenshot, whose
   *  dimensions aren't stored). */
  srcWidth: number;
  srcHeight: number;
  /** the recipe already applied (so re-opening shows the current edit). */
  initialRecipe?: EditRecipe | null;
  title?: string;
  /** true while the parent bakes + uploads (disables Apply, shows a spinner). */
  busy?: boolean;
  /** true while the parent RESOLVES the source (PDF→raster / fetch-as-blob). */
  preparing?: boolean;
  /** a source-resolution error message (PDF couldn't be read, etc.) or null. */
  loadError?: string | null;
  /** Apply → the parent bakes this recipe + uploads the derived image. */
  onApply: (recipe: EditRecipe) => void;
  onCancel: () => void;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  // When the caller doesn't know the source size (e.g. the north screenshot),
  // read the image's natural size on load so the crop SVG maps 1:1 to pixels.
  const [measured, setMeasured] = useState<{ w: number; h: number } | null>(
    () => (srcWidth > 0 && srcHeight > 0 ? { w: srcWidth, h: srcHeight } : null),
  );
  const W = measured?.w ?? (srcWidth > 0 ? srcWidth : 800);
  const H = measured?.h ?? (srcHeight > 0 ? srcHeight : 600);
  const ready = measured != null;

  const [deg, setDeg] = useState<number>(() =>
    normaliseDeg(initialRecipe?.rotateDeg ?? 0),
  );
  // DISPLAYED (rotated-bounding-box) frame — the axis-aligned space the upright
  // crop is drawn + held in, and the space `bakeImage` (rotate→crop) crops. The
  // <img> is rotated + centred inside a box sized to this frame; the crop SVG is
  // axis-aligned with viewBox `0 0 dispW dispH`.
  const { width: dispW, height: dispH } = useMemo(
    () => rotatedBBox(W, H, deg),
    [W, H, deg],
  );
  const [crop, setCrop] = useState<CropRect | null>(
    () =>
      clampCrop(
        initialRecipe?.crop ?? null,
        rotatedBBox(W, H, normaliseDeg(initialRecipe?.rotateDeg ?? 0)).width,
        rotatedBBox(W, H, normaliseDeg(initialRecipe?.rotateDeg ?? 0)).height,
      ),
  );
  // Live drag state lives in a REF, not state — the window-level move listener
  // (below) reads it without stale-closure bugs and without re-binding on every
  // render. `dragRef` is the source of truth.
  //
  // ── WHY NATIVE MOUSE/TOUCH, NOT THE POINTER EVENTS API (load-bearing) ──
  // The drag previously used the Pointer Events API (onPointerDown +
  // setPointerCapture, later window pointermove/up). On REAL desktop mice in
  // Safari AND Chrome (DevTools closed) the crop drag silently produced NO
  // selection — it only "worked" under Chrome DevTools device mode, which
  // emulates TOUCH pointers. The pointer-event path was the variable: capturing
  // the pointer on the SVG hit-rect and then re-rendering that rect (a crop
  // appears on the first move) dropped the capture, so pointermove stopped
  // firing for a real mouse. We removed pointer events ENTIRELY: a mousedown on
  // the SVG attaches window mousemove/mouseup, and a touchstart attaches window
  // touchmove/touchend. Plain mouse + touch events are rock-solid on every
  // desktop browser and every mobile/Capacitor webview, and never depend on
  // pointer capture — so the drag works identically everywhere.
  const dragRef = useRef<CropDrag | null>(null);
  // The exact listener instances added for the active drag, so teardown removes
  // the SAME function references (handlers are re-created each render; without
  // this, removeEventListener would miss and leak). Cleared on drag end. The
  // drag is either MOUSE or TOUCH; we store whichever pair we attached.
  const listenersRef = useRef<{
    move: (e: MouseEvent) => void;
    up: (e: MouseEvent) => void;
    touchMove?: (e: TouchEvent) => void;
    touchEnd?: (e: TouchEvent) => void;
  } | null>(null);

  // Esc cancels; lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel, busy]);

  // Safety net: if the modal unmounts mid-drag (Apply/Cancel/Esc while dragging),
  // remove any window mouse/touch listeners we attached so they never leak.
  useEffect(() => {
    return () => {
      const ls = listenersRef.current;
      if (ls) {
        window.removeEventListener('mousemove', ls.move);
        window.removeEventListener('mouseup', ls.up);
        if (ls.touchMove) window.removeEventListener('touchmove', ls.touchMove);
        if (ls.touchEnd) {
          window.removeEventListener('touchend', ls.touchEnd);
          window.removeEventListener('touchcancel', ls.touchEnd);
        }
        listenersRef.current = null;
      }
      dragRef.current = null;
    };
  }, []);

  const recipe: EditRecipe = useMemo(
    () => ({ crop, rotateDeg: deg }),
    [crop, deg],
  );
  const out = useMemo(() => bakedSize(recipe, W, H), [recipe, W, H]);
  const dirty = !recipeIsIdentity(recipe) ||
    !recipeIsIdentity(initialRecipe ?? null);

  // Straightening (a rotation change) reshapes the displayed (rotated-bbox)
  // frame the crop lives in. The owner straightens the image UNDER a fixed,
  // upright crop box and wants to KEEP SEEING the region inside it — the crop
  // must PERSIST through rotation, NOT be wiped (owner-locked 2026-06-15;
  // reverses the earlier "clear on rotate" model) and NOT tilt with the image
  // (the SVG overlay stays axis-aligned). As the frame reshapes we re-map the
  // upright crop from the OLD displayed frame into the NEW one (proportional
  // rescale + clamp) so it stays in the same relative spot and always visible,
  // rather than discarding it. (Skip the very first run so an initialRecipe's
  // crop survives mount.)
  const prevFrameRef = useRef({ deg, dispW, dispH });
  useEffect(() => {
    const prev = prevFrameRef.current;
    if (prev.deg !== deg) {
      prevFrameRef.current = { deg, dispW, dispH };
      // Re-map the existing crop into the new straightened frame (persist, not
      // clear). A null crop stays null.
      setCrop((c) =>
        rescaleCropToFrame(c, prev.dispW, prev.dispH, dispW, dispH),
      );
    } else {
      // keep the cached frame in sync (e.g. measured size arriving) without
      // re-mapping when the angle didn't change.
      prevFrameRef.current = { deg, dispW, dispH };
    }
  }, [deg, dispW, dispH]);

  function rotate(by: number) {
    setDeg((d) => normaliseDeg(d + by));
  }
  function setRotateExact(v: string) {
    const n = Number(v);
    if (Number.isFinite(n)) setDeg(normaliseDeg(n));
  }

  /* ── crop drag (in the rotated-bbox / displayed frame) — NATIVE mouse + touch ──
   *
   * Browser-agnostic drag with NO Pointer Events API: a mousedown (or
   * touchstart) begins the drag and attaches window-level mousemove/mouseup
   * (or touchmove/touchend) listeners; the listeners resolve the crop from
   * `dragRef.current` (the live drag) and the displayed-frame position, then tear
   * themselves down on up/end. Mouse events are rock-solid on every desktop
   * browser (no setPointerCapture, no pointer-capture-drop on re-render — the
   * bug class that broke real-mouse dragging in Safari AND Chrome); touch events
   * cover mobile / Capacitor. */
  function endDrag() {
    dragRef.current = null;
    const ls = listenersRef.current;
    if (ls) {
      window.removeEventListener('mousemove', ls.move);
      window.removeEventListener('mouseup', ls.up);
      if (ls.touchMove) window.removeEventListener('touchmove', ls.touchMove);
      if (ls.touchEnd) {
        window.removeEventListener('touchend', ls.touchEnd);
        window.removeEventListener('touchcancel', ls.touchEnd);
      }
      listenersRef.current = null;
    }
  }
  /** Apply one move sample (a client point) to the live drag → update the crop. */
  function applyMove(pt: ClientPt) {
    const d = dragRef.current;
    if (!d) return;
    const p = toSrcPt(svgRef.current, pt);
    if (!p) return;
    setCrop(resolveDrag(d, p.x, p.y, dispW, dispH));
  }
  /** Finish the drag: discard a too-tiny rect (a click, not a drag), tear down. */
  function finishDrag() {
    if (dragRef.current) {
      // Functional updater so we read the freshest crop the move just set.
      setCrop((c) => {
        const cc = clampCrop(c, dispW, dispH);
        return cc && cc.w >= 8 && cc.h >= 8 ? cc : null;
      });
    }
    endDrag();
  }
  /** Begin a drag from a single client point + a drag kind. Attaches BOTH the
   *  window mousemove/up AND touchmove/end listeners so the same drag works for
   *  a mouse or a finger; whichever fires first drives it, and `finishDrag`
   *  tears all of them down. The listener instances are stored in `listenersRef`
   *  so teardown removes the exact same references; `dragRef` is read inside the
   *  move listeners, so they always see the latest drag (no stale closure). */
  function beginDrag(drag: CropDrag) {
    endDrag(); // defensively clear any prior drag's listeners
    dragRef.current = drag;
    const move = (ev: MouseEvent) => {
      // a real desktop drag holds the primary button; if it was released
      // outside our up handler (e.g. focus loss), end cleanly.
      if (ev.buttons === 0) {
        finishDrag();
        return;
      }
      ev.preventDefault();
      applyMove(ev);
    };
    const up = () => finishDrag();
    const touchMove = (ev: TouchEvent) => {
      const t = firstTouch(ev);
      if (!t) return;
      ev.preventDefault(); // stop the page from scrolling under the finger
      applyMove(t);
    };
    const touchEnd = () => finishDrag();
    listenersRef.current = { move, up, touchMove, touchEnd };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    // touchmove must be NON-passive so preventDefault() can stop page scroll.
    window.addEventListener('touchmove', touchMove, { passive: false });
    window.addEventListener('touchend', touchEnd);
    window.addEventListener('touchcancel', touchEnd);
  }
  /** Resolve a down event (mouse OR touch) to a displayed-frame point + start `kind`. */
  function startFrom(
    e: React.MouseEvent | React.TouchEvent,
    make: (p: { x: number; y: number }) => CropDrag,
    seed?: (p: { x: number; y: number }) => void,
  ) {
    const client: ClientPt | null =
      'touches' in e
        ? (e.touches[0] ?? e.changedTouches[0] ?? null)
        : { clientX: e.clientX, clientY: e.clientY };
    if (!client) return;
    const p = toSrcPt(svgRef.current, client);
    if (!p) return;
    seed?.(p);
    beginDrag(make(p));
  }
  function onDown(e: React.MouseEvent | React.TouchEvent) {
    if (busy || !ready) return;
    // ignore a non-primary mouse button (right/middle).
    if ('button' in e && e.button !== 0) return;
    e.preventDefault();
    startFrom(
      e,
      (p) => ({ kind: 'new', sx: p.x, sy: p.y }),
      // start a fresh zero-size rect from the down point.
      (p) => setCrop({ x: p.x, y: p.y, w: 0, h: 0 }),
    );
  }
  function onCornerDown(e: React.MouseEvent | React.TouchEvent, corner: Corner) {
    if (busy || !crop) return;
    if ('button' in e && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    beginDrag({ kind: 'corner', corner, rect: crop });
  }
  function onBodyDown(e: React.MouseEvent | React.TouchEvent) {
    if (busy || !crop) return;
    if ('button' in e && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const cropNow = crop;
    startFrom(e, (p) => ({
      kind: 'move',
      ox: p.x - cropNow.x,
      oy: p.y - cropNow.y,
      rect: cropNow,
    }));
  }

  // marching-ants handle size, scaled to the displayed frame so it reads at any
  // zoom / rotation.
  const hr = Math.max(6, Math.min(dispW, dispH) / 60);

  const cornerPts = crop
    ? ([
        { c: 0 as Corner, x: crop.x, y: crop.y },
        { c: 1 as Corner, x: crop.x + crop.w, y: crop.y },
        { c: 2 as Corner, x: crop.x + crop.w, y: crop.y + crop.h },
        { c: 3 as Corner, x: crop.x, y: crop.y + crop.h },
      ] as { c: Corner; x: number; y: number }[])
    : [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(20,20,24,0.62)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onMouseDown={(e) => {
        // backdrop click cancels (but not a drag that bubbled up)
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 'var(--r-md, 10px)',
          boxShadow: '0 18px 60px rgba(0,0,0,0.32)',
          width: 'min(960px, 96vw)',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid var(--rule)',
          }}
        >
          <strong style={{ fontSize: 15 }}>{title}</strong>
          <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            Source {W}×{H}px → output {out.width}×{out.height}px
            {deg ? ` · straightened ${deg.toFixed(deg % 1 ? 1 : 0)}°` : ''}
          </span>
        </div>

        {/* rotate controls */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: '1px solid var(--rule)',
          }}
        >
          <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Rotate</span>
          <Btn3 intent="ghost" size="sm" onClick={() => rotate(-90)} disabled={busy}>
            ⟲ 90°
          </Btn3>
          <Btn3 intent="ghost" size="sm" onClick={() => rotate(90)} disabled={busy}>
            ⟳ 90°
          </Btn3>
          <Btn3 intent="ghost" size="sm" onClick={() => rotate(45)} disabled={busy}>
            ⟳ 45°
          </Btn3>
          <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--rule)' }} />
          <Btn3 intent="ghost" size="sm" onClick={() => rotate(-1)} disabled={busy} aria-label="Nudge minus one degree">
            −1°
          </Btn3>
          <input
            type="number"
            value={Number(deg.toFixed(2))}
            min={0}
            max={360}
            step={1}
            aria-label="Rotation in degrees"
            onChange={(e) => setRotateExact(e.target.value)}
            disabled={busy}
            style={{
              width: 80,
              padding: '5px 8px',
              border: '1px solid var(--rule-strong)',
              borderRadius: 'var(--r-sm)',
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
          <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>deg</span>
          <Btn3 intent="ghost" size="sm" onClick={() => rotate(1)} disabled={busy} aria-label="Nudge plus one degree">
            +1°
          </Btn3>
          <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--rule)' }} />
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setCrop(null)}
            disabled={busy || !crop}
            title="Clear the crop rectangle (keep the whole image)"
          >
            Clear crop
          </Btn3>
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => {
              setDeg(0);
              setCrop(null);
            }}
            disabled={busy}
            title="Reset rotate + crop"
          >
            Reset
          </Btn3>
        </div>

        {/* stage: source image (rotated/straightened) + AXIS-ALIGNED crop SVG
            (the upright crop lives in the rotated-bbox frame `dispW×dispH`) */}
        <div
          style={{
            flex: '1 1 auto',
            overflow: 'auto',
            background: '#f3f1ec',
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 220,
          }}
        >
          {/* Source resolution states: a PDF is rasterised + every source is
              fetched-as-blob (CORS-safe) before it can be shown/baked. Until
              that finishes (or if it fails) we show a clear state, never a
              broken <img>. */}
          {(preparing || !imageUrl) && !loadError && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-mute)',
                textAlign: 'center',
                lineHeight: 1.6,
                padding: 24,
              }}
            >
              Preparing the image…
              <div style={{ fontSize: 11.5, marginTop: 4 }}>
                Rendering the plan for editing (PDFs are rasterised first).
              </div>
            </div>
          )}
          {loadError && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--crit, #dc2626)',
                textAlign: 'center',
                lineHeight: 1.6,
                padding: 24,
                maxWidth: 440,
              }}
            >
              Could not load that file to edit.
              <div style={{ fontSize: 11.5, marginTop: 4, color: 'var(--ink-mute)' }}>
                {loadError}
              </div>
            </div>
          )}
          {!preparing && !loadError && imageUrl && (
          <div
            style={{
              position: 'relative',
              // ROTATE-THEN-CROP STAGE (2026-06-14, Anil) — the stage box is the
              // DISPLAYED (rotated-bounding-box) frame `dispW×dispH`, so the whole
              // straightened image (including its rotated corners) is reachable and
              // the axis-aligned crop SVG maps 1:1 to the crop space. The box keeps
              // the dispW:dispH ratio (never squashes) and is capped to 820px wide
              // / 60vh tall — width is the smaller of the two caps so a tall frame
              // letterboxes instead of squashing; height follows from aspectRatio.
              aspectRatio: `${dispW} / ${dispH}`,
              width: `min(820px, 100%, calc(60vh * ${dispW} / ${dispH}))`,
              flex: '0 1 auto',
            }}
          >
            <img
              src={imageUrl}
              alt="Source to crop and rotate"
              draggable={false}
              onLoad={(e) => {
                if (!measured) {
                  const im = e.currentTarget;
                  if (im.naturalWidth > 0 && im.naturalHeight > 0) {
                    setMeasured({ w: im.naturalWidth, h: im.naturalHeight });
                  }
                }
              }}
              style={{
                // The source <img> is placed at its NATURAL W:H proportion as a
                // fraction of the dispW×dispH box (so it is NOT stretched), centred,
                // and rotated by `deg`. Its rotated bounding box then exactly fills
                // the box — i.e. the box IS the rotated-bbox frame. (W/dispW and
                // H/dispH are the un-rotated element size in box units.)
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: `${(W / dispW) * 100}%`,
                height: `${(H / dispH) * 100}%`,
                objectFit: 'fill',
                transform: `translate(-50%, -50%) rotate(${deg}deg)`,
                transformOrigin: 'center center',
                userSelect: 'none',
                opacity: 0.96,
              }}
            />
            <svg
              ref={svgRef}
              viewBox={`0 0 ${dispW} ${dispH}`}
              preserveAspectRatio="none"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                // AXIS-ALIGNED OVERLAY (2026-06-14, Anil — owner-locked
                // "rotate THEN crop"): the crop SVG carries NO rotation. The user
                // straightens the image (the <img> above rotates), then draws a
                // NORMAL UPRIGHT crop box over it — the overlay stays axis-aligned
                // to the screen at every angle ("I never asked you to rotate the
                // crop window"). The crop rect lives in the rotated-bbox frame
                // (viewBox `0 0 dispW dispH`); `bakeImage` ROTATES the source first
                // THEN crops this upright rect out of the straightened image.
                // (The earlier build rotated the overlay WITH the image — that
                // tilted the crop window, which the owner rejected. REVERTED.)
                cursor: crop ? 'default' : 'crosshair',
                touchAction: 'none',
              }}
              onMouseDown={onDown}
              onTouchStart={onDown}
            >
              {/* Always-present transparent hit area: browsers do NOT fire
                  mouse/touch events on UNPAINTED svg regions, so without this the
                  first drag (before any crop exists) never reaches onDown and no
                  selection forms ("dragging does nothing"). A painted
                  (transparent-fill) rect makes the whole layer a hit target; the
                  crop/handle rects paint on top of it. */}
              <rect x={0} y={0} width={dispW} height={dispH} fill="transparent" pointerEvents="all" />
              {/* dim everything outside the crop rect (overlay-with-hole) */}
              {crop && (
                <>
                  <defs>
                    <mask id="crop-hole">
                      <rect x={0} y={0} width={dispW} height={dispH} fill="#fff" />
                      <rect
                        x={crop.x}
                        y={crop.y}
                        width={crop.w}
                        height={crop.h}
                        fill="#000"
                      />
                    </mask>
                  </defs>
                  <rect
                    x={0}
                    y={0}
                    width={dispW}
                    height={dispH}
                    fill="rgba(20,20,24,0.45)"
                    mask="url(#crop-hole)"
                    pointerEvents="none"
                  />
                  <rect
                    x={crop.x}
                    y={crop.y}
                    width={crop.w}
                    height={crop.h}
                    fill="transparent"
                    stroke="#2f5d8a"
                    strokeWidth={Math.max(1.5, Math.min(dispW, dispH) / 300)}
                    vectorEffect="non-scaling-stroke"
                    onMouseDown={onBodyDown}
                    onTouchStart={onBodyDown}
                    style={{ cursor: 'move' }}
                  />
                  {cornerPts.map((c) => (
                    <rect
                      key={`c${c.c}`}
                      x={c.x - hr}
                      y={c.y - hr}
                      width={hr * 2}
                      height={hr * 2}
                      fill="#2f5d8a"
                      stroke="#fff"
                      strokeWidth={Math.max(1, Math.min(dispW, dispH) / 400)}
                      onMouseDown={(e) => onCornerDown(e, c.c)}
                      onTouchStart={(e) => onCornerDown(e, c.c)}
                      style={{ cursor: 'nwse-resize' }}
                    />
                  ))}
                </>
              )}
            </svg>
          </div>
          )}
        </div>

        {/* footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 16px',
            borderTop: '1px solid var(--rule)',
          }}
        >
          <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', lineHeight: 1.4 }}>
            Draw an upright box to crop, then rotate to straighten — the box stays
            put and keeps showing what's inside. The original upload is kept; a new
            edited image is used for tracing and the report.
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn3 intent="ghost" size="sm" onClick={onCancel} disabled={busy}>
              Cancel
            </Btn3>
            <Btn3
              intent="grn"
              size="sm"
              onClick={() => onApply(recipe)}
              disabled={busy || preparing || !!loadError || !ready || !dirty}
              title={
                loadError
                  ? 'The source could not be loaded'
                  : preparing
                    ? 'Preparing the image…'
                    : dirty
                      ? 'Bake the edited image'
                      : 'No change to apply'
              }
            >
              {busy ? 'Applying…' : 'Apply edit'}
            </Btn3>
          </div>
        </div>
      </div>
    </div>
  );
}
